import React from 'react';
import { useTranslation } from 'react-i18next';
import { RefLabel } from '@/components/refLabel';
import { Icon } from '@iconify/react';
import { DataGrid, NumberValue, Widget } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import { formatGel } from '@/utils/money';
import { formatInLanguage } from '@/utils/dateLocale';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { JournalEntryLink } from '@/components/journalEntryLink';
import { InvoiceTotal } from '../invoiceTotal';
import { ImportHistoryMethodChip } from './ImportHistoryMethodChip';
import {
    formatInvoiceNumber,
    isAutoInvoice,
    isSettled,
    outstandingAmount,
    type ImportHistoryEntry,
} from './importHistoryMeta';

type ImportHistoryViewProps = {
    items: ImportHistoryEntry[];
};

/**
 * Read-only history of every invoice import: a `Method` chip (Excel /
 * Invoice / Manual), the receiving `Branch`, the invoice number, supplier,
 * date, product count and total amount. The header icon tile uses a
 * neutral `foreground/10` background — history is reference data, not an
 * in-progress or successful state worth highlighting. Rows have no pinned
 * actions; this is a read-only audit log.
 */
export const ImportHistoryView: React.FC<ImportHistoryViewProps> = ({
    items,
}) => {
    const { t, i18n } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('purchases.widgets.importHistory.title'),
            subtitle: (count: number) =>
                t('purchases.widgets.importHistory.subtitle', { count }),
            tableAria: t('purchases.widgets.importHistory.tableAria'),
            settled: t('purchases.widgets.importHistory.settled'),
            outstandingOf: t('purchases.widgets.importHistory.outstandingOf'),
            columns: {
                invoice: t('purchases.widgets.importHistory.columns.invoice'),
                supplier: t('purchases.widgets.importHistory.columns.supplier'),
                date: t('purchases.widgets.importHistory.columns.date'),
                productCount: t(
                    'purchases.widgets.importHistory.columns.productCount',
                ),
                paid: t('purchases.widgets.importHistory.columns.paid'),
                totalAmount: t(
                    'purchases.widgets.importHistory.columns.totalAmount',
                ),
                importDate: t(
                    'purchases.widgets.importHistory.columns.importDate',
                ),
                method: t('purchases.widgets.importHistory.columns.method'),
                journalEntry: t(
                    'purchases.widgets.importHistory.columns.journalEntry',
                ),
                branch: t('purchases.widgets.importHistory.columns.branch'),
            },
            noBranch: {
                label: t('purchases.widgets.importHistory.noBranch'),
            },
            empty: {
                title: t('purchases.widgets.importHistory.empty.title'),
            },
        }),
        [t],
    );

    const columns = React.useMemo<DataGridColumn<ImportHistoryEntry>[]>(
        () => [
            {
                id: 'invoice',
                header: copy.columns.invoice,
                isRowHeader: true,
                width: 150,
                accessorKey: 'invoiceNumber',
                allowsSorting: true,
                cell: (item) => (
                    <span
                        className="font-mono text-xs font-bold text-foreground"
                        {...getNodeTestIdProp(
                            `import-history-row-number-${item.id}`,
                        )}
                    >
                        {formatInvoiceNumber(item.invoiceNumber)}
                    </span>
                ),
            },
            {
                id: 'supplier',
                header: copy.columns.supplier,
                accessorKey: 'supplier',
                allowsSorting: true,
                cell: (item) => (
                    <span
                        className="truncate text-sm text-foreground"
                        {...getNodeTestIdProp(
                            `import-history-row-supplier-${item.id}`,
                        )}
                    >
                        <RefLabel
                            archived={item.supplierArchived}
                            deleted={item.supplierDeleted}
                            name={item.supplier}
                        />
                    </span>
                ),
            },
            {
                id: 'date',
                header: copy.columns.date,
                width: 150,
                accessorKey: 'date',
                allowsSorting: true,
                cell: (item) => (
                    <span
                        className="text-xs text-muted"
                        {...getNodeTestIdProp(
                            `import-history-row-date-${item.id}`,
                        )}
                    >
                        {formatInLanguage(
                            item.date,
                            'D MMM YYYY · HH:mm',
                            i18n.language,
                        )}
                    </span>
                ),
            },
            {
                id: 'productCount',
                header: copy.columns.productCount,
                width: 110,
                align: 'end',
                accessorKey: 'productCount',
                allowsSorting: true,
                cell: (item) => (
                    <NumberValue
                        className="text-sm tabular-nums text-foreground"
                        maximumFractionDigits={0}
                        minimumFractionDigits={0}
                        style="decimal"
                        value={item.productCount}
                        {...getNodeTestIdProp(
                            `import-history-row-product-count-${item.id}`,
                        )}
                    />
                ),
            },
            {
                id: 'totalAmount',
                header: copy.columns.totalAmount,
                width: 140,
                align: 'end',
                accessorKey: 'totalAmount',
                allowsSorting: true,
                cell: (item) => (
                    <InvoiceTotal
                        expensesOnInvoiceTotalTetri={
                            item.expensesOnInvoiceTotalTetri
                        }
                        expensesTotalTetri={item.expensesTotalTetri}
                        grossTotalTetri={item.grossTotalTetri}
                        linesTotalTetri={item.linesTotalTetri}
                        payableTotalTetri={item.payableTotalTetri}
                        rowId={item.id}
                        slot="import-history-row"
                        totalAmountTetri={item.totalAmountTetri}
                        vatTotalTetri={item.vatTotalTetri}
                    />
                ),
            },
            {
                id: 'paid',
                header: copy.columns.paid,
                width: 150,
                align: 'end',
                accessorKey: 'paidAmount',
                allowsSorting: true,
                // Paid on top, outstanding underneath: the question a reader
                // brings to this column is "is it settled?", and the answer is
                // the second line, not the first.
                cell: (item) => (
                    <div className="flex flex-col items-end gap-0.5">
                        <span
                            className="text-sm tabular-nums text-foreground"
                            {...getNodeTestIdProp(
                                `import-history-row-paid-${item.id}`,
                            )}
                        >
                            {formatGel(item.paidAmountTetri)}
                        </span>
                        <span
                            className={`text-[11px] tabular-nums ${
                                isSettled(item)
                                    ? 'text-success-soft-foreground'
                                    : 'text-danger-soft-foreground'
                            }`}
                            {...getNodeTestIdProp(
                                `import-history-row-outstanding-${item.id}`,
                            )}
                        >
                            {isSettled(item)
                                ? copy.settled
                                : `${copy.outstandingOf} ${formatGel(outstandingAmount(item))}`}
                        </span>
                    </div>
                ),
            },
            {
                id: 'importDate',
                header: copy.columns.importDate,
                width: 160,
                accessorKey: 'importedAt',
                allowsSorting: true,
                cell: (item) => (
                    <span
                        className="text-xs text-muted"
                        {...getNodeTestIdProp(
                            `import-history-row-import-date-${item.id}`,
                        )}
                    >
                        {formatInLanguage(
                            item.importedAt,
                            'D MMM YYYY · HH:mm',
                            i18n.language,
                        )}
                    </span>
                ),
            },
            {
                id: 'method',
                header: copy.columns.method,
                width: 130,
                accessorKey: 'method',
                allowsSorting: true,
                cell: (item) => (
                    <ImportHistoryMethodChip
                        entryId={item.id}
                        isAuto={isAutoInvoice(item.invoiceNumber)}
                        method={item.method}
                    />
                ),
            },
            {
                id: 'journalEntry',
                header: copy.columns.journalEntry,
                width: 150,
                cell: (item) => (
                    <JournalEntryLink
                        date={item.date}
                        entryId={item.journalEntryId}
                        slot={`import-history-row-${item.id}`}
                    />
                ),
            },
            {
                id: 'branch',
                header: copy.columns.branch,
                width: 180,
                accessorKey: 'branchName',
                allowsSorting: true,
                cell: (item) => (
                    <span
                        className="truncate text-sm text-foreground"
                        {...getNodeTestIdProp(
                            `import-history-row-branch-${item.id}`,
                        )}
                    >
                        <RefLabel
                            absentLabel={copy.noBranch.label}
                            archived={item.branchArchived}
                            deleted={item.branchDeleted}
                            name={item.branchName}
                        />
                    </span>
                ),
            },
        ],
        [copy, i18n.language],
    );

    return (
        <section
            aria-labelledby="import-history-widget-title"
            {...getNodeTestIdProp('import-history-section')}
        >
            <Widget
                className="flex flex-col gap-0 overflow-hidden p-0"
                {...getNodeTestIdProp('import-history-widget')}
            >
                <Widget.Header
                    className="mt-4"
                    {...getNodeTestIdProp('import-history-widget-header')}
                >
                    <Widget.Title
                        className="flex items-center gap-2"
                        {...getNodeTestIdProp('import-history-widget-title')}
                    >
                        <div
                            aria-hidden
                            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-foreground/10 text-foreground"
                            {...getNodeTestIdProp('import-history-icon-wrap')}
                        >
                            <Icon
                                className="size-4"
                                icon="solar:history-bold-duotone"
                                {...getNodeTestIdProp('import-history-icon')}
                            />
                        </div>
                        {copy.title}
                    </Widget.Title>
                    <Widget.Description
                        {...getNodeTestIdProp('import-history-widget-subtitle')}
                    >
                        {copy.subtitle(items.length)}
                    </Widget.Description>
                </Widget.Header>
                <DataGrid<ImportHistoryEntry>
                    aria-label={copy.tableAria}
                    columns={columns}
                    data={items}
                    defaultSortDescriptor={{
                        column: 'importDate',
                        direction: 'descending',
                    }}
                    getRowId={(item) => item.id}
                    renderEmptyState={() => (
                        <div
                            className="flex flex-col items-center gap-3 py-12 text-muted"
                            {...getNodeTestIdProp('import-history-empty')}
                        >
                            <Icon
                                aria-hidden
                                className="size-8 text-default-500"
                                icon="solar:history-bold-duotone"
                                {...getNodeTestIdProp(
                                    'import-history-empty-icon',
                                )}
                            />
                            <span
                                className="text-sm"
                                {...getNodeTestIdProp(
                                    'import-history-empty-title',
                                )}
                            >
                                {copy.empty.title}
                            </span>
                        </div>
                    )}
                    scrollContainerClassName="max-h-[520px] overflow-y-auto"
                    variant="primary"
                    {...getNodeTestIdProp('import-history-grid')}
                />
            </Widget>
        </section>
    );
};
